import React, { useReducer, useEffect } from "react";
import Cookies from "js-cookie";
import axios2 from "axios";
import { ConfigProvider, App as AppAntd } from "antd";
import dayjs from "dayjs";
import jalaliPlugin from 'jalali-plugin-dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { JalaliLocaleListener } from "antd-jalali";
import language from "./resources/languages/fa_IR";
import { AntDesign, ActionType, API, Enum } from "./resources/constants";

// Components
import NeshanMapLeaflet from './components/neshan-map-leaflet';
import NeshanMapOpenlayers from './components/neshan-map-openlayers';
import PendingRequest from './components/pending-request'

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "./interfaces/axios"
import { InternalAxiosRequestConfig, AxiosResponse } from "axios"

// Reducers
import GlobalReducer from './reducers/global'
import AxiosReducer from './reducers/axios'

// Contexts
import GlobalContext from './contexts/global'
import AxiosContext from './contexts/axios'

const { SET_PENDING_REQUESTS, SET_USER, SET_FORM, SET_TRAVEL, SET_IS_OPEN_FLOAT_BUTTON, SET_PROVINCES, SET_CARS, SET_OWNERS, SET_OPEN_DRAWER, SET_OPEN_TOUR } = ActionType
const { USER_MANAGEMENT_AUTH_PROFILE, TRAVEL_TRAVEL_PAGINATE, TRAVEL_TRAVEL_PROVINCE, TRAVEL_TRAVEL_CAR, TRAVEL_TRAVEL_OWNER, TRAVEL_TRAVEL_VIEW } = API;
const { Form, TravelMod, Payment } = Enum
const { Dayjs: DayjsLanguage } = language;
const urlParams = new URLSearchParams(window.location.search)

dayjs.extend(jalaliPlugin);
dayjs.extend(updateLocale);
dayjs.updateLocale('fa', DayjsLanguage);

export default function App() {

    const [{
        openDrawer,
        openTour,
        destination,
        destinationLocation,
        origin,
        originLocation,
        directions,
        startDirectionsIcon,
        endDirectionsIcon,
        form,
        user,
        provinces,
        owners,
        cars,
        filter,
        travel,
        travelType,
        travelMod,
        isOpenFloatButton,
    }, dispatchGlobal] = useReducer(GlobalReducer , {
        destination: null,
        destinationLocation: null,
        origin: null,
        originLocation: null,
        openDrawer: false,
        openTour: false,
        directions: null,
        startDirectionsIcon: null,
        endDirectionsIcon: null,
        form: Form.FILTER,
        user: null,
        owners: [],
        cars: [],
        provinces: [],
        filter: {},
        travel: {},
        travelType: null,
        travelMod: TravelMod.NEW,
        isOpenFloatButton: false,
    })

    const [{
        pendingRequests,
        axios,
    }, dispatchAxios] = useReducer(AxiosReducer , {
        pendingRequests: 0,
        axios: axios2.create({
            baseURL: process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_API_URL : process.env.REACT_APP_PRODUCTION_API_URL,
            timeout: Number(process.env.REACT_APP_TIME_OUT),
            withCredentials: true,
        })
    })

    const pendingRequest = (counter: number) => {
        const element = document.getElementById('pending-request')
        if (element) element.style.display = counter ? "flex" : "none";
    }

    const redirectPSP = (payment: string, token: string) => {
        switch (payment.toUpperCase()) {
            case Payment.Zarinpal:
                window.location.replace(`${process.env.REACT_APP_ZARINPAL_PAYMENT_START_PAY}${token}`);
                break;
            case Payment.Sep:
                window.location.replace(`${process.env.REACT_APP_SEP_PAYMENT_START_PAY}?token=${token}`);
                break;
            case Payment.Pep:
                window.location.replace(`${process.env.REACT_APP_PEP_PAYMENT_START_PAY}${token}`);
                break;
        }
    }

    useEffect(() => {
        const psp = urlParams.get('psp');
        const token = urlParams.get('token');

        // Redirect to psp url
        if (psp && token) redirectPSP(psp, token)

        let counter = 0;

        // All api request
        axios.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                //dispatchAxios({ type: SET_PENDING_REQUESTS , payload: { plus: true }})
                pendingRequest(++counter)
                const accessToken = Cookies.get("accessToken")
                config.headers.Authorization = accessToken && `Bearer ${accessToken}`
                return config
            },
            (error: any) => Promise.reject(error)
        );
        // All api response
        axios.interceptors.response.use(
            (response: AxiosResponse ) => {
                //dispatchAxios({ type: SET_PENDING_REQUESTS , payload: { plus: false }})
                pendingRequest(--counter)
                return response
            },
            (error: AxiosError) => {
                const { response: { status } } = error
                if (status === 401) {
                    Cookies.remove('accessToken')
                    dispatchGlobal({ type: SET_USER , payload: { user: null }})
                }

                //dispatchAxios({ type: SET_PENDING_REQUESTS , payload: { plus: false }})
                pendingRequest(--counter)
                return Promise.reject(error)
            }
        );
        axios.get(TRAVEL_TRAVEL_PROVINCE).then((response: { data: [] }) => {
            const { data: provinces } = response
            dispatchGlobal({ type: SET_PROVINCES , payload: { provinces }})
        }).catch( (error: AxiosError) => error );
        axios.get(TRAVEL_TRAVEL_OWNER).then((response: { data: [] }) => {
            const { data: owners } = response
            dispatchGlobal({ type: SET_OWNERS , payload: { owners }})
        }).catch( (error: AxiosError) => error );
        axios.get(TRAVEL_TRAVEL_CAR).then((response: { data: [] }) => {
            const { data: cars } = response
            dispatchGlobal({ type: SET_CARS , payload: { cars }})
        }).catch( (error: AxiosError) => error );
        axios.get(TRAVEL_TRAVEL_VIEW).then((response: { data: '' }) => response).catch( (error: AxiosError) => error );
        axios.post(TRAVEL_TRAVEL_PAGINATE, filter, { params: { page: 1, limit: 1 }}).then((response: AxiosResponsePaginate) => {
            const { data, data: { docs } } = response
            const travel =  docs.length ? { ...docs[0], paginate: { ...data, docs: null } } : {}
            dispatchGlobal({ type: SET_TRAVEL , payload: { travel }})
        }).catch( (error: AxiosError) => error );
        axios.get(USER_MANAGEMENT_AUTH_PROFILE).then((response: { data: { complete: boolean } }) => {
            const { data: user } = response
            dispatchGlobal({ type: SET_USER , payload: { user }})
            if (!user.complete) {
                dispatchGlobal({ type: SET_FORM , payload: { form: Form.PROFILE }})
                dispatchGlobal({ type: SET_OPEN_TOUR , payload: { openTour: true }})
            } else if (urlParams.get('payment')) {
                dispatchGlobal({ type: SET_FORM , payload: { form: Form.PAYMENT }})
                dispatchGlobal({ type: SET_OPEN_DRAWER , payload: { openDrawer: true }})
            } else {
                dispatchGlobal({ type: SET_FORM , payload: { form: Form.FILTER }})
                dispatchGlobal({ type: SET_OPEN_DRAWER , payload: { openDrawer: true }})
            }
        }).catch( (error: AxiosError) => {
            if (urlParams.get('payment')) {
                dispatchGlobal({ type: SET_FORM , payload: { form: Form.SIGN_IN }})
                dispatchGlobal({ type: SET_OPEN_DRAWER , payload: { openDrawer: true }})
            } else {
                dispatchGlobal({ type: SET_OPEN_TOUR , payload: { openTour: true }})
            }
        });
    }, [])

    return (
      <ConfigProvider
        direction={"rtl"}
        locale={language}
        theme={AntDesign.theme}
      >
          <AppAntd>
          <JalaliLocaleListener />
          <PendingRequest pendingRequests={pendingRequests}/>
          <GlobalContext.Provider value={{
              openDrawer,
              openTour,
              destination,
              destinationLocation,
              origin,
              originLocation,
              directions,
              startDirectionsIcon,
              endDirectionsIcon,
              form,
              user,
              provinces,
              owners,
              cars,
              filter,
              travel,
              travelType,
              travelMod,
              isOpenFloatButton,
              dispatch: dispatchGlobal
          }}>
              <AxiosContext.Provider value={{
                  pendingRequests,
                  axios,
                  dispatch: dispatchAxios
              }}>
                  {
                      process.env.REACT_APP_MAP === 'neshan_map_l' ? <NeshanMapLeaflet /> : process.env.REACT_APP_MAP === 'neshan_map_ol' ? <NeshanMapOpenlayers /> : null
                  }
              </AxiosContext.Provider>
          </GlobalContext.Provider>
          </AppAntd>
      </ConfigProvider>
    )
}
